import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import styles from './EventSalesPage.module.css';
import { useTranslation } from 'react-i18next';

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const formatYen = (value) => `¥${toNumber(value).toLocaleString('ja-JP')}`;

const EventSalesPage = () => {
  const { id } = useParams();
  const { t } = useTranslation('sales');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [csvLoading, setCsvLoading] = useState(false);

  const payoutLabel = (payoutStatus, donationStatus, gross, donationCount) => {
    const hasTicketSales = Number(gross) > 0;
    const hasDonation = Number(donationCount) > 0;
    if (!hasTicketSales && hasDonation) {
      if (donationStatus === 'paid') return t('payout.paid');
      if (donationStatus === 'partial') return t('payout.partial');
      if (donationStatus === 'scheduled') return t('payout.scheduled');
      return t('payout.unpaid');
    }
    if (hasTicketSales && !hasDonation) {
      if (payoutStatus === 'paid') return t('payout.paid');
      if (payoutStatus === 'partial') return t('payout.partial');
      if (payoutStatus === 'scheduled') return t('payout.scheduled');
      return t('payout.unpaid');
    }
    if (hasTicketSales && hasDonation) {
      if (payoutStatus === 'paid' && (!donationStatus || donationStatus === 'paid')) return t('payout.paid');
      if (payoutStatus === 'partial' || donationStatus === 'partial') return t('payout.partial');
      if (payoutStatus === 'scheduled' || donationStatus === 'scheduled') return t('payout.scheduled');
      return t('payout.unpaid');
    }
    return t('payout.unpaid');
  };

  const feeBearerLabel = (value) => (value === 'buyer' ? t('feeBearerBuyer') : t('feeBearerOrganizer'));

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/events/${id}/sales-summary`);
        setData(res.data || null);
      } catch (e) {
        if (e?.response?.status === 403) {
          setError(t('errorForbidden'));
        } else {
          setError(t('errorLoad'));
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) load();
  }, [id]);

  const rows = useMemo(() => {
    const groups = Array.isArray(data?.groups) ? data.groups : [];
    return groups;
  }, [data]);

  const handleDownloadCsv = async () => {
    if (!id) return;

    setCsvLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/events/${id}/sales-summary.csv`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales_summary_event_${id}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      window.alert(t('csvFailed'));
    } finally {
      setCsvLoading(false);
    }
  };

  if (loading) {
    return <div className={styles.page}><div className={styles.container}>{t('loading')}</div></div>;
  }

  if (error) {
    return <div className={styles.page}><div className={styles.container}>{error}</div></div>;
  }

  const eventName = data?.event?.name || t('pageTitle');
  const eventDate = String(data?.event?.date || '').slice(0, 10);
  const venue = data?.event?.venue || '-';
  const feeBearer = data?.event?.fee_bearer || 'organizer';
  // payoutLabelに売上・投げ銭件数も渡す
  const payout = payoutLabel(
    data?.payout?.display_status,
    data?.donation?.payout_status,
    data?.gross,
    data?.donation?.count
  );

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>{t('pageTitle')}</h1>
            <p className={styles.eventName}>{eventName}</p>
            <p className={styles.meta}>{eventDate}</p>
            <p className={styles.meta}>{venue}</p>
            <p className={styles.meta}>{t('feeBearer')}: {feeBearerLabel(feeBearer)}</p>
          </div>
          <div className={styles.headerActions}>
            <span className={styles.payoutBadge}>{payout}</span>
            <button type="button" className={styles.csvButton} onClick={handleDownloadCsv} disabled={csvLoading}>
              {csvLoading ? t('csvGenerating') : t('csvDownload')}
            </button>
            <Link className={styles.backLink} to="/dashboard">{t('backToDashboard')}</Link>
          </div>
        </header>

        <section className={styles.ticketSummary}>
          <h2 className={styles.ticketSummaryTitle}>🎫 {t('tickets.sectionTitle')}</h2>
          <div className={styles.cards}>
            <article className={styles.card}>
              <div className={styles.cardLabel}>{t('tickets.grossLabel')}</div>
              <div className={styles.cardValue}>{formatYen(data?.gross)}</div>
              <div className={styles.cardSub}>{t('tickets.soldLabel')}: {toNumber(data?.total_quantity)}{t('tickets.soldUnit')}</div>
            </article>
            <article className={styles.card}>
              <div className={styles.cardLabel}>{t('tickets.stripeFeeLabel')}</div>
              <div className={styles.cardValue}>{formatYen(data?.stripe_fee_total)}</div>
            </article>
            <article className={styles.card}>
              <div className={styles.cardLabel}>{t('tickets.platformFeeLabel')}</div>
              <div className={styles.cardValue}>{formatYen(data?.platform_fee_total)}</div>
            </article>
          </div>
        </section>

        {toNumber(data?.donation?.count) > 0 && (
          <section className={styles.donationSummary}>
            <h2 className={styles.donationSummaryTitle}>💝 {t('donation.sectionTitle')}</h2>
            <div className={styles.donationCards}>
              <article className={styles.donationCard}>
                <div className={styles.cardLabel}>{t('donation.countLabel')}</div>
                <div className={styles.cardValue}>{toNumber(data?.donation?.count)}{t('donation.countUnit')}</div>
              </article>
              <article className={styles.donationCard}>
                <div className={styles.cardLabel}>{t('donation.grossLabel')}</div>
                <div className={styles.cardValue}>{formatYen(data?.donation?.gross)}</div>
              </article>
              <article className={styles.donationCard}>
                <div className={styles.cardLabel}>{t('donation.feeLabel')}</div>
                <div className={styles.cardValue}>{formatYen(data?.donation?.fee_total)}</div>
              </article>
            </div>
          </section>
        )}

        <section className={styles.payoutRow}>
          <h2 className={styles.payoutRowTitle}>{t('payout.sectionTitle')}</h2>
          <article className={`${styles.card} ${styles.cardNet}`}>
            <div className={styles.cardLabel}>
              {toNumber(data?.donation?.count) > 0 ? t('payout.labelWithDonation') : t('payout.label')}
            </div>
            <div className={styles.cardValue}>{formatYen(toNumber(data?.net_total) + toNumber(data?.donation?.net_total))}</div>
          </article>
        </section>

        <section className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
              <th>{t('table.priceType')}</th>
                <th>{t('table.unitPrice')}</th>
                <th>{t('table.quantity')}</th>
                <th>{t('table.gross')}</th>
                <th>{t('table.stripeFee')}</th>
                <th>{t('table.platformFee')}</th>
                <th>{t('table.refund')}</th>
                <th>{t('table.chargebackAmount')}</th>
                <th>{t('table.chargebackFee')}</th>
                <th>{t('table.net')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((group) => (
                <React.Fragment key={group.ticket_category}>
                  <tr className={styles.groupRow}>
                    <td colSpan={10}>{group.ticket_category}</td>
                  </tr>
                  {group.items.map((item) => (
                    <tr key={`${group.ticket_category}-${item.price_type}-${item.unit_price}`} className={item.has_refund_impact ? styles.refundRow : ''}>
                      {(() => {
                        const displayNet = toNumber(item.net);
                        return (
                          <>
                            <td>{item.price_type}</td>
                            <td>{formatYen(item.unit_price)}</td>
                            <td>{toNumber(item.quantity)}</td>
                            <td>{formatYen(item.gross)}</td>
                            <td>{formatYen(item.stripe_fee)}</td>
                            <td>{formatYen(item.platform_fee)}</td>
                            <td>{formatYen(item.refund)}</td>
                            <td>{formatYen(item.chargeback_amount)}</td>
                            <td>{formatYen(item.chargeback_fee)}</td>
                            <td className={displayNet < 0 ? styles.negativeNet : styles.positiveNet}>{formatYen(displayNet)}</td>
                          </>
                        );
                      })()}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={10} className={styles.empty}>{t('table.empty')}</td>
                </tr>
              )}
              {toNumber(data?.donation?.count) > 0 && (
                <React.Fragment key="donation">
                  <tr className={styles.groupRow}>
                    <td colSpan={10}>💝 {t('donation.tableCategory')}</td>
                  </tr>
                  <tr>
                    <td>{t('donation.tableType')}</td>
                    <td>-</td>
                    <td>{toNumber(data?.donation?.count)}{t('donation.tableCount')}</td>
                    <td>{formatYen(data?.donation?.gross)}</td>
                    <td>-</td>
                    <td>{formatYen(data?.donation?.fee_total)}</td>
                    <td>-</td>
                    <td>-</td>
                    <td>-</td>
                    <td className={styles.positiveNet}>{formatYen(data?.donation?.net_total)}</td>
                  </tr>
                </React.Fragment>
              )}
            </tbody>
          </table>
        </section>
      </div>
    </div>
  );
};

export default EventSalesPage;
